import { useEffect, useState } from 'react'
import {
  LocalAudioTrack,
  LocalAudioTrackPublication,
  LocalVideoTrackPublication,
  Participant,
  RemoteAudioTrack,
  RemoteAudioTrackPublication,
  RemoteVideoTrack,
  RemoteVideoTrackPublication,
  TwilioError,
} from 'twilio-video'

import ErrorBoundary from '../../../containers/error_boundary'
import { useCallManager } from '../../../contexts/call_manager_context'
import { useParticipantManager } from '../../../contexts/participant_manager_context'
import { CS } from '../call_manager/connection_status_reducer'
import { useIsLocalParticipant } from '../participant_manager/use_is_local_participant'

// Get the published audio track for a participant (local or remote participant)
export const useAudioTrack = (
  participant?: Participant | null,
  doAttach = false,
): LocalAudioTrack | RemoteAudioTrack | null => {
  const [localAudioPublication, setLocalAudioPublication] =
    useState<LocalAudioTrackPublication | null>(null)
  const [audioTrack, setAudioTrack] = useState<
    LocalAudioTrack | RemoteAudioTrack | null
  >(null)

  const isLocalParticipant = useIsLocalParticipant(participant)
  const {
    activeCall: { connectionStatus, prevConnectionStatus },
  } = useCallManager()
  const { localTrackData: { audioOutputDeviceId } = {} } =
    useParticipantManager()

  useEffect(() => {
    return () => {
      setLocalAudioPublication(null)
      setAudioTrack(null)
    }
  }, [participant])

  useEffect(() => {
    if (participant) {
      const publication = participant.audioTracks.entries().next().value?.[1]
      if (publication) {
        if (isLocalParticipant) {
          setLocalAudioPublication(publication)
        }
        setAudioTrack(publication?.track)
      }
    }
  }, [isLocalParticipant, participant])

  useEffect(() => {
    if (audioTrack && doAttach) {
      const attachAudioTrack = () => {
        const audioElement = audioTrack.attach()
        if (audioOutputDeviceId) {
          // eslint-disable-next-line
          // @ts-ignore
          audioElement.setSinkId?.(audioOutputDeviceId).then(() => {
            document.body.appendChild(audioElement)
          })
        }
      }
      audioTrack.on('started', attachAudioTrack)
      if (audioTrack.isStarted) {
        attachAudioTrack()
      }
      return () => {
        audioTrack?.detach()
        audioTrack.off('started', attachAudioTrack)
      }
    }
  }, [audioOutputDeviceId, audioTrack, doAttach])

  // responsible for nullifying track, as track is unpublished, but not stopped
  useEffect(() => {
    if (
      (prevConnectionStatus === CS.ROOM_CONNECTED ||
        prevConnectionStatus === CS.ROOM_RECONNECTING) &&
      connectionStatus === CS.CALL_CONNECTED &&
      audioTrack
    ) {
      setAudioTrack(null)
      if (doAttach) {
        audioTrack.detach()
      }
    }
  }, [
    audioTrack,
    connectionStatus,
    doAttach,
    participant,
    prevConnectionStatus,
  ])

  // For remote participant
  useEffect(() => {
    if (participant && !isLocalParticipant) {
      const trackSubscribed = (track: RemoteAudioTrack | RemoteVideoTrack) => {
        if (track.kind === 'audio') {
          setAudioTrack(track)
        }
      }

      const trackUnsubscribed = (
        track: RemoteAudioTrack | RemoteVideoTrack,
      ) => {
        if (track.kind === 'audio') {
          setAudioTrack(null)
          if (doAttach) {
            track.detach()
          }
        }
      }

      const trackSubscriptionFailed = (
        error: TwilioError,
        publication: RemoteAudioTrackPublication | RemoteVideoTrackPublication,
      ) => {
        if (publication.kind === 'audio') {
          ErrorBoundary.logErrorToMyService(error, publication)
        }
      }

      participant.on('trackSubscribed', trackSubscribed)
      participant.on('trackUnsubscribed', trackUnsubscribed)
      participant.on('trackSubscriptionFailed', trackSubscriptionFailed)

      return () => {
        participant.off('trackSubscribed', trackSubscribed)
        participant.off('trackUnsubscribed', trackUnsubscribed)
        participant.off('trackSubscriptionFailed', trackSubscriptionFailed)
      }
    }
  }, [participant, doAttach, isLocalParticipant])

  // For local participant
  useEffect(() => {
    if (participant && isLocalParticipant) {
      const trackPublished = (
        publication: LocalAudioTrackPublication | LocalVideoTrackPublication,
      ) => {
        if (publication.kind === 'audio') {
          setLocalAudioPublication(publication)
          const track = publication.track
          setAudioTrack(track)
        }
      }

      participant.on('trackPublished', trackPublished)

      return () => {
        participant.off('trackPublished', trackPublished)
      }
    }
  }, [isLocalParticipant, participant])

  useEffect(() => {
    // using localAudioPublication to determine if it is a local participant,
    // as isLocalParticipant is set to undefined, listener is removed, but not reattached causing the cleanup to fail
    if (localAudioPublication && audioTrack) {
      const onStarted = (track: LocalAudioTrack) => {
        setLocalAudioPublication(Object.create(track))
      }

      // TODO: https://github.com/twilio/twilio-video.js/issues/1607
      const onStopped = (track: LocalAudioTrack) => {
        localAudioPublication?.unpublish()
        setLocalAudioPublication(null)
        setAudioTrack(null)
        if (doAttach) {
          track.detach()
        }
      }
      audioTrack.on('started', onStarted)
      audioTrack.on('stopped', onStopped)

      return () => {
        audioTrack.off('started', onStarted)
        audioTrack.off('stopped', onStopped)
      }
    }
  }, [localAudioPublication, audioTrack, doAttach])

  return audioTrack
}
